import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Poppins } from 'src/common/Poppins';
import { Spacer } from 'src/common/Spacer';
import { GradientTextAction } from 'src/common/GradientText';
import { MOTION_TRANSITION } from 'src/config';
import { IntelNote } from '../types';

// Collapsed preview height for the basis-of-estimate prose. The RIE narrative can run many
// paragraphs, so clamp it to a few lines and reveal the rest behind "See more".
const CLAMP_LINES = 4;

const Line = styled(Poppins)`
  display: block;
  line-height: 1.7;
  /* The note may carry \n\n paragraph breaks — preserve them instead of collapsing to a space. */
  white-space: pre-wrap;
`;

// The basis prose, optionally clamped to CLAMP_LINES with an ellipsis while collapsed.
const BasisLine = styled(Line)<{ $clamped: boolean }>`
  ${({ $clamped }) =>
    $clamped &&
    `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${CLAMP_LINES};
    overflow: hidden;
  `}
`;

// "See more / See less" toggle — subtle opacity fade on hover, matching the other gradient actions.
const ExpandToggle = styled(GradientTextAction)`
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

// Whether a note has anything worth rendering. False when the engine hasn't supplied the text yet
// (e.g. an empty frequency `suggested_narrative`), so callers can skip it instead of drawing a blank line.
export const noteHasContent = (note: IntelNote): boolean =>
  note.basisOfEstimate.trim().length > 0 ||
  note.supportingResearch.length > 0 ||
  (note.consider?.length ?? 0) > 0 ||
  !!note.manualUpdateStatement;

export const QuantNote: React.FC<{ note: IntelNote }> = ({ note }) => {
  const [open, setOpen] = useState(false);
  const basisRef = useRef<HTMLSpanElement>(null);
  const [basisOverflows, setBasisOverflows] = useState(false);

  // Structured detail (supporting research / consider / manual statement) is reveal-on-expand.
  // The live wire note is a flat string with none of these, so it's clamped on the prose instead.
  const hasStructured =
    note.supportingResearch.length > 0 || (note.consider?.length ?? 0) > 0 || !!note.manualUpdateStatement;

  // Clamp the prose only when there's nothing structured to reveal — when the BE supplies structured
  // notes we keep the original "full text + reveal research" behaviour.
  const clampBasis = !hasStructured && !open;

  // Measure whether the clamped prose is actually clipped, so we don't show "See more" on a short note.
  // Only meaningful while collapsed (the element is unclamped once open), so skip the measure then and
  // keep the last value — the toggle must stay rendered while expanded.
  useLayoutEffect(() => {
    const el = basisRef.current;
    if (!el || !clampBasis) return;
    setBasisOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [note.basisOfEstimate, clampBasis]);

  // Nothing to show — render nothing rather than an empty line / blank gap.
  if (!noteHasContent(note)) return null;

  const expandable = hasStructured || basisOverflows;

  return (
    <div>
      <BasisLine ref={basisRef} px={16} color="cflowerBlue" $clamped={clampBasis}>
        {note.basisOfEstimate}
      </BasisLine>

      {expandable && (
        <>
          <Spacer $px={8} />
          <ExpandToggle px={16} weight={500} onClick={() => setOpen((o) => !o)}>
            {open ? 'See less' : 'See more'}
          </ExpandToggle>
        </>
      )}

      {hasStructured && (
      <motion.div
        initial={false}
        animate={{ height: open ? 'auto' : 0 }}
        transition={MOTION_TRANSITION}
        style={{ overflow: 'hidden' }}
      >
        <Spacer $px={16} />
        <Poppins px={14} weight={600} color="prussianBlue">
          Quantification Supporting Research
        </Poppins>
        <Spacer $px={8} />
        {note.supportingResearch.map((r, i) => (
          <Line key={r.sourceTitle} px={14} color="cflowerBlue">
            {i + 1}. {r.sourceTitle}
            {r.year ? ` (${r.year})` : ''} found that {r.statistic}
            {r.confidence != null ? ` Confidence ${r.confidence}%` : ''}
          </Line>
        ))}

        {note.consider && note.consider.length > 0 && (
          <>
            <Spacer $px={12} />
            <Poppins px={14} weight={600} color="prussianBlue">
              Consider
            </Poppins>
            <Spacer $px={8} />
            {note.consider.map((c) => (
              <Line key={c} px={14} color="cflowerBlue">
                - {c}
              </Line>
            ))}
          </>
        )}

        {note.manualUpdateStatement && (
          <>
            <Spacer $px={12} />
            <Line px={14} color="cflowerBlue" css="font-style: italic;">
              {note.manualUpdateStatement}
            </Line>
          </>
        )}
      </motion.div>
      )}
    </div>
  );
};
